import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, ClipboardText, Plus, CircleNotch, ArrowSquareOut, CheckCircle,
  Clock, Lightning, Flame, Notepad, PhoneCall
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALL_SCRIPTS = {
  permit_request: {
    title: "Permit Application Call",
    script: `Hello, this is [YOUR NAME] with [COMPANY NAME], contractor license #[LICENSE].
I'm calling to apply for a [PERMIT TYPE] permit.
Property address: [ADDRESS]
Scope of work: [SCOPE]
Estimated project cost: [COST]
When can I schedule the inspection?`,
  },
  inspection_request: {
    title: "Inspection Request Call",
    script: `Hello, this is [YOUR NAME] with [COMPANY NAME].
I need to schedule a [INSPECTION TYPE] inspection.
Permit number: [PERMIT #]
Property address: [ADDRESS]
Preferred date: [DATE]
My callback number is [PHONE].`,
  },
  inspection_followup: {
    title: "Inspection Follow-up Call",
    script: `Hello, this is [YOUR NAME] with [COMPANY NAME].
I'm following up on permit [PERMIT #] inspection.
The inspection was scheduled for [DATE].
Can you confirm the status?`,
  },
  customer_followup: {
    title: "Customer Satisfaction Call",
    script: `Hello [CUSTOMER NAME], this is [YOUR NAME] from [COMPANY NAME].
I'm following up on the [JOB TYPE] work we completed at your property.
Is everything working well? Do you have any concerns?
If you're satisfied, would you mind leaving us a brief review? 
I can text you the link. Thank you for your business!`,
  }
};

function PermitCard({ permit, onCall, onUpdate }) {
  const isElec = permit.permit_type === "electrical";
  return (
    <div data-testid={`permit-${permit.id}`} className={`wp-card animate-slide-up ${isElec ? "border-l-2 border-l-[#002FA7]" : "border-l-2 border-l-[#FF5A00]"}`}>
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{permit.permit_code}</h3>
          <p className="text-xs text-[#404040]">{permit.property_address}</p>
        </div>
        <span className={`font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm ${
          permit.status === "approved" ? "text-[#34C759] bg-[#34C75910]" :
          permit.status === "submitted" ? "text-[#002FA7] bg-[#002FA710]" :
          "text-[#404040] bg-[#F4F4F5]"
        }`}>{permit.status?.toUpperCase()}</span>
      </div>
      <p className="text-[11px] text-[#404040] mb-2">{permit.scope_of_work}</p>
      <div className="flex gap-2">
        <button onClick={() => onCall(permit, "permit_request")} className="flex-1 py-1.5 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm flex items-center justify-center gap-1">
          <Phone size={10} /> Call for Permit
        </button>
        <button onClick={() => onCall(permit, "inspection_request")} className="flex-1 py-1.5 text-[10px] font-bold border border-[#E5E5E5] rounded-sm flex items-center justify-center gap-1">
          <ClipboardText size={10} /> Request Inspection
        </button>
      </div>
      {permit.call_log?.length > 0 && (
        <div className="mt-2 border-t border-[#E5E5E5] pt-1">
          <span className="font-mono-data text-[10px] text-[#404040]">{permit.call_log.length} CALLS LOGGED</span>
        </div>
      )}
    </div>
  );
}

export default function PermitsPage() {
  const [permits, setPermits] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [permitTypes, setPermitTypes] = useState({});
  const [portalUrl, setPortalUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callNotes, setCallNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [form, setForm] = useState({
    job_id: "", permit_type: "electrical", permit_code: "", property_address: "",
    property_type: "residential", contractor_name: "", contractor_license: "", scope_of_work: "", estimated_cost: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [permitsRes, typesRes, jobsRes] = await Promise.all([
        axios.get(`${API}/permits`),
        axios.get(`${API}/permits/types`),
        axios.get(`${API}/jobs`),
      ]);
      setPermits(permitsRes.data);
      setPermitTypes(typesRes.data.permit_types || {});
      setPortalUrl(typesRes.data.portal_url || "");
      setJobs(jobsRes.data);
    } catch { toast.error("Failed to load permits"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createPermit = async (e) => {
    e.preventDefault();
    if (!form.permit_code || !form.property_address) { toast.error("Permit type and address required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/permits`, form);
      toast.success("Permit request created");
      setCreateOpen(false);
      setForm({ job_id: "", permit_type: "electrical", permit_code: "", property_address: "", property_type: "residential", contractor_name: "", contractor_license: "", scope_of_work: "", estimated_cost: "" });
      fetchData();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const startCall = (permit, callType) => {
    const script = CALL_SCRIPTS[callType];
    setActiveCall({ permit, callType, script });
    setCallNotes("");
    setCallOutcome("");
    setCallOpen(true);
  };

  const makePhoneCall = (number) => {
    window.location.href = `tel:${number}`;
  };

  const logCall = async () => {
    if (!activeCall) return;
    try {
      await axios.post(`${API}/permits/${activeCall.permit.id}/log-call`, {
        call_type: activeCall.callType,
        phone_number: "City of Lubbock Permits",
        notes: callNotes,
        outcome: callOutcome,
        script_used: activeCall.script?.title
      });
      toast.success("Call logged");
      setCallOpen(false);
      fetchData();
    } catch { toast.error("Failed to log call"); }
  };

  const ptCodes = permitTypes[form.permit_type] || [];

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="permits-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>Permits</h1>
          <p className="text-[10px] font-mono-data text-[#404040]">APPLICATIONS & INSPECTIONS</p>
        </div>
        <div className="flex gap-2">
          {portalUrl && (
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="portal-link"
              className="px-2 py-1.5 text-[10px] font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] flex items-center gap-1">
              <ArrowSquareOut size={10} /> Lubbock Portal
            </a>
          )}
          <button data-testid="create-permit-btn" onClick={() => setCreateOpen(true)}
            className="px-2 py-1.5 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C]">+ New Permit</button>
        </div>
      </div>

      {/* Quick Call Buttons */}
      <div className="wp-card mb-4" data-testid="quick-calls">
        <h3 className="font-mono-data text-[10px] text-[#002FA7] mb-2">QUICK DIAL</h3>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => makePhoneCall("+18067753158")} data-testid="call-lubbock-permits"
            className="py-2 text-xs font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] flex items-center justify-center gap-1">
            <PhoneCall size={12} className="text-[#002FA7]" /> Permits 806-775-3158
          </button>
          <button onClick={() => makePhoneCall("+18067752087")} data-testid="call-lubbock-inspections"
            className="py-2 text-xs font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] flex items-center justify-center gap-1">
            <PhoneCall size={12} className="text-[#FF5A00]" /> Inspections 806-775-2087
          </button>
        </div>
      </div>

      <Tabs defaultValue="permits">
        <TabsList className="grid w-full grid-cols-2 h-9 mb-4">
          <TabsTrigger value="permits" className="text-xs" data-testid="permits-tab">Permits ({permits.length})</TabsTrigger>
          <TabsTrigger value="scripts" className="text-xs" data-testid="scripts-tab">Call Scripts</TabsTrigger>
        </TabsList>

        <TabsContent value="permits">
          {loading ? <div className="flex justify-center py-12"><CircleNotch size={28} className="animate-spin text-[#002FA7]" /></div> :
          permits.length === 0 ? <div className="wp-card text-center py-8 text-sm text-[#404040]">No permits yet. Create one to start.</div> :
          <div className="space-y-3">{permits.map((p,i) => <div key={p.id} style={{animationDelay:`${i*0.05}s`}}><PermitCard permit={p} onCall={startCall} /></div>)}</div>}
        </TabsContent>

        <TabsContent value="scripts">
          <div className="space-y-3">
            {Object.entries(CALL_SCRIPTS).map(([key, script]) => (
              <div key={key} className="wp-card">
                <h3 className="text-sm font-bold mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>{script.title}</h3>
                <pre className="text-[11px] text-[#404040] whitespace-pre-wrap bg-[#F4F4F5] p-2 rounded-sm font-mono">{script.script}</pre>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Permit Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>New Permit Request</DialogTitle></DialogHeader>
          <form onSubmit={createPermit} className="space-y-3">
            <Select value={form.permit_type} onValueChange={v => setForm({...form, permit_type: v, permit_code: ""})}>
              <SelectTrigger><SelectValue placeholder="Trade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="hvac">HVAC / Mechanical</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.permit_code} onValueChange={v => setForm({...form, permit_code: v})}>
              <SelectTrigger data-testid="permit-code-select"><SelectValue placeholder="Permit Type" /></SelectTrigger>
              <SelectContent>
                {ptCodes.map(p => <SelectItem key={p.code} value={p.code}>{p.name} ({p.fee_est})</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="text" placeholder="Property Address" value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Contractor Name" value={form.contractor_name} onChange={e => setForm({...form, contractor_name: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
              <input type="text" placeholder="License #" value={form.contractor_license} onChange={e => setForm({...form, contractor_license: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            </div>
            <textarea placeholder="Scope of Work" value={form.scope_of_work} onChange={e => setForm({...form, scope_of_work: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16" />
            <input type="text" placeholder="Estimated Cost" value={form.estimated_cost} onChange={e => setForm({...form, estimated_cost: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <button data-testid="create-permit-submit" type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm disabled:opacity-50">{saving ? "Creating..." : "Create Permit Request"}</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Call Script Dialog */}
      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>{activeCall?.script?.title}</DialogTitle></DialogHeader>
          {activeCall && (
            <div className="space-y-3">
              <div className="p-3 bg-[#F4F4F5] rounded-sm">
                <span className="font-mono-data text-[10px] text-[#002FA7]">CALL SCRIPT</span>
                <pre className="text-[11px] text-[#0A0A0A] whitespace-pre-wrap font-mono mt-1">{activeCall.script.script}</pre>
              </div>
              <button onClick={() => makePhoneCall("+18067753158")} data-testid="dial-now-btn"
                className="w-full py-3 bg-[#34C759] text-white text-sm font-bold rounded-sm flex items-center justify-center gap-2">
                <Phone size={16} /> Call Lubbock Permits 806-775-3158
              </button>
              <textarea placeholder="Call notes..." value={callNotes} onChange={e => setCallNotes(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16" />
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger><SelectValue placeholder="Call Outcome" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="permit_approved">Permit Approved</SelectItem>
                  <SelectItem value="inspection_scheduled">Inspection Scheduled</SelectItem>
                  <SelectItem value="left_message">Left Message</SelectItem>
                  <SelectItem value="on_hold">On Hold / Pending</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={logCall} data-testid="log-call-btn"
                className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm">Log Call & Close</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
